import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';

/**
 * Redaction rejects (HTTP 400 with `{ code: "memory_redacted_reject" }`) are
 * an expected user-flow outcome, not a bug — the thunk catches them and shows
 * a localized toast. Skip the error-level log at the service layer so F12
 * doesn't light up red for a legitimate rejection.
 */
function isExpectedRedactionReject(error: unknown): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e: any = error;
  const status =
    e?.originalError?.response?.status ?? e?.response?.status ?? e?.statusCode;
  if (status !== 400) return false;
  const data = e?.originalError?.response?.data ?? e?.response?.data;
  return data?.code === 'memory_redacted_reject';
}
import {
  API_CHAT_USER_MEMORY_PREFERENCES,
  API_CHAT_USER_MEMORY_PREFERENCE_BY_KEY,
  API_CHAT_USER_MEMORY_RECENT_ENTITIES,
  API_CHAT_USER_MEMORY_RECENT_ENTITY_BY_ID,
  API_CHAT_USER_MEMORY_FACTS,
  API_CHAT_USER_MEMORY_FACT_BY_ID,
} from '../../constants/apiConstants';

// ============================================================================
// DTOs — mirror the backend output payloads in
// SmartMatrix.Domain/Core/Chat/Messages/ChatUser{Preference,RecentEntity,Fact}_Messages.cs
// .NET serializes with PropertyNamingPolicy = CamelCase (see Program.cs).
// ============================================================================

export interface ChatUserPreference {
  id: number;
  prefKey: string;
  prefValue: string;
  valueType: string;
  source: string;
  createdAt?: string;
  modifiedAt?: string;
}

export interface ChatUserRecentEntity {
  id: number;
  entityType: string;
  entityKey: string;
  entityLabel: string;
  lastSeenAt: string;
  seenCount: number;
  sourceSessionId?: number;
}

export interface ChatUserFact {
  id: number;
  factText: string;
  tags: string[];
  sourceSessionId?: number;
  createdAt?: string;
  modifiedAt?: string;
}

interface ListResult<T> {
  items: T[];
  totalCount: number;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build the query-param object passed to apiClient. When `tenantId` is null we
 * intentionally omit the key entirely — the backend treats a missing tenant
 * query as the null-tenant scope (a valid distinct memory bucket per
 * Requirement 11.3).
 */
function tenantParams(tenantId: number | null): Record<string, unknown> {
  return tenantId == null ? {} : { tenantId };
}

function unwrap<T>(data: unknown): T {
  // Some controllers wrap in Result<T> with `succeeded` + `data`; others
  // return the bare payload. Match the Spec B session service tolerance.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = data as any;
  return (d?.data ?? d) as T;
}

// ============================================================================
// Service
// ============================================================================

const chatUserMemoryService = {
  // ---------------- Preferences ----------------
  async listPreferences(tenantId: number | null): Promise<ChatUserPreference[]> {
    try {
      const response = await apiClient.get(API_CHAT_USER_MEMORY_PREFERENCES, {
        params: tenantParams(tenantId),
      });
      const payload = unwrap<ListResult<ChatUserPreference>>(response.data);
      return payload?.items ?? [];
    } catch (error) {
      logger.error('chatUserMemoryService.listPreferences', getErrorMessage(error));
      throw error;
    }
  },

  async upsertPreference(
    key: string,
    value: string,
    valueType: string,
    tenantId: number | null
  ): Promise<ChatUserPreference | null> {
    try {
      const response = await apiClient.put(
        API_CHAT_USER_MEMORY_PREFERENCE_BY_KEY(key),
        { prefKey: key, prefValue: value, valueType },
        { params: tenantParams(tenantId) }
      );
      // Controller returns `result.Data?.Item` directly — i.e. the raw item.
      return unwrap<ChatUserPreference>(response.data) ?? null;
    } catch (error) {
      if (isExpectedRedactionReject(error)) {
        logger.debug('chatUserMemoryService.upsertPreference redacted', getErrorMessage(error));
      } else {
        logger.error('chatUserMemoryService.upsertPreference', getErrorMessage(error));
      }
      throw error;
    }
  },

  async deletePreference(key: string, tenantId: number | null): Promise<void> {
    try {
      await apiClient.delete(API_CHAT_USER_MEMORY_PREFERENCE_BY_KEY(key), {
        params: tenantParams(tenantId),
      });
    } catch (error) {
      logger.error('chatUserMemoryService.deletePreference', getErrorMessage(error));
      throw error;
    }
  },

  // ---------------- Recent entities ----------------
  async listRecentEntities(
    take: number | undefined,
    tenantId: number | null
  ): Promise<ChatUserRecentEntity[]> {
    try {
      const response = await apiClient.get(API_CHAT_USER_MEMORY_RECENT_ENTITIES, {
        params: { ...tenantParams(tenantId), take },
      });
      const payload = unwrap<ListResult<ChatUserRecentEntity>>(response.data);
      return payload?.items ?? [];
    } catch (error) {
      logger.error('chatUserMemoryService.listRecentEntities', getErrorMessage(error));
      throw error;
    }
  },

  async deleteRecentEntity(id: number, tenantId: number | null): Promise<void> {
    try {
      await apiClient.delete(API_CHAT_USER_MEMORY_RECENT_ENTITY_BY_ID(id), {
        params: tenantParams(tenantId),
      });
    } catch (error) {
      logger.error('chatUserMemoryService.deleteRecentEntity', getErrorMessage(error));
      throw error;
    }
  },

  async clearAllRecentEntities(tenantId: number | null): Promise<void> {
    try {
      await apiClient.delete(API_CHAT_USER_MEMORY_RECENT_ENTITIES, {
        params: tenantParams(tenantId),
      });
    } catch (error) {
      logger.error('chatUserMemoryService.clearAllRecentEntities', getErrorMessage(error));
      throw error;
    }
  },

  // ---------------- Facts ----------------
  async listFacts(
    tag: string | undefined,
    tenantId: number | null
  ): Promise<ChatUserFact[]> {
    try {
      const response = await apiClient.get(API_CHAT_USER_MEMORY_FACTS, {
        params: { ...tenantParams(tenantId), tag: tag || undefined },
      });
      const payload = unwrap<ListResult<ChatUserFact>>(response.data);
      return payload?.items ?? [];
    } catch (error) {
      logger.error('chatUserMemoryService.listFacts', getErrorMessage(error));
      throw error;
    }
  },

  async createFact(
    factText: string,
    tags: string[],
    sourceSessionId: number | undefined,
    tenantId: number | null
  ): Promise<ChatUserFact | null> {
    try {
      const response = await apiClient.post(
        API_CHAT_USER_MEMORY_FACTS,
        { factText, tags, sourceSessionId },
        { params: tenantParams(tenantId) }
      );
      return unwrap<ChatUserFact>(response.data) ?? null;
    } catch (error) {
      if (isExpectedRedactionReject(error)) {
        logger.debug('chatUserMemoryService.createFact redacted', getErrorMessage(error));
        throw error;
      }
      logger.error('chatUserMemoryService.createFact', getErrorMessage(error));
      throw error;
    }
  },

  async updateFact(
    id: number,
    factText: string,
    tags: string[],
    tenantId: number | null
  ): Promise<ChatUserFact | null> {
    try {
      const response = await apiClient.put(
        API_CHAT_USER_MEMORY_FACT_BY_ID(id),
        { factText, tags },
        { params: tenantParams(tenantId) }
      );
      return unwrap<ChatUserFact>(response.data) ?? null;
    } catch (error) {
      if (isExpectedRedactionReject(error)) {
        logger.debug('chatUserMemoryService.updateFact redacted', getErrorMessage(error));
        throw error;
      }
      logger.error('chatUserMemoryService.updateFact', getErrorMessage(error));
      throw error;
    }
  },

  async deleteFact(id: number, tenantId: number | null): Promise<void> {
    try {
      await apiClient.delete(API_CHAT_USER_MEMORY_FACT_BY_ID(id), {
        params: tenantParams(tenantId),
      });
    } catch (error) {
      logger.error('chatUserMemoryService.deleteFact', getErrorMessage(error));
      throw error;
    }
  },
};

export default chatUserMemoryService;
